import { useState, useMemo, useEffect } from "react";
import { MAP_LAYER_COLORS, MAP_LAYER_LABELS } from "../../constants/mapLayers";
import { pinField, asNumber } from "../../utils/pinFields";
import { loadAssignedComps, loadRentRoll, loadAddressDossier } from "../../supabase";
import PropertyMedia from "../pipeline/PropertyMedia";
import { leaseCompsNear, saleCompsAt, metersBetween } from "../../utils/geometry";

// ═══════════════════════════════════════════════════════════════════════════
// PROPERTY PAGE
// ═══════════════════════════════════════════════════════════════════════════
// Keyed by ADDRESS, not by record. An address is the durable identity — it
// can be a sourcing property today, a deal next quarter and a comp after it
// trades — so everything Proxy knows about the building belongs on one page:
// the property record, the deal if there is one, its rent roll and assigned
// comps, sale and lease comps at the address, contacts and outreach history.
//
// Every pin at a building opens this same page.
//
// The rule from the earlier record-keyed version still holds: a field with no
// value does not render, a section with no populated fields does not render,
// and a tab with nothing behind it does not appear. A thin address gets a
// short honest page rather than a wall of dashes.

const NEARBY_LEASE_METERS = 200;

const money = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${n.toLocaleString()}`); };
const psf   = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : `$${n.toFixed(2)}`; };
const pct   = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : `${n.toFixed(1)}%`; };
const sf    = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : `${n.toLocaleString()} SF`; };
const num   = v => { const n = asNumber(v); return n == null ? null : n.toLocaleString(); };
const date  = v => (v ? String(v).slice(0, 10) : null);
const text  = v => (v == null || v === "" ? null : String(v));
const mult  = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : `${n.toFixed(2)}x`; };

// Property row first, then the pin — the imported property record is the
// fuller source for building attributes, but a deal-only address has none.
function fieldOf(property, pin, key) {
  const fromProp = property ? property[key] : null;
  if (fromProp !== undefined && fromProp !== null && fromProp !== "") return fromProp;
  return pin ? pinField(pin, key) : null;
}

// The scroll area's top padding. Named because the sticky pill row has to
// cancel it exactly - see the note there - and two numbers drifting apart is
// how that row went see-through in the first place.
const SCROLL_PAD_TOP = 18;

// The slider's top stop, and the value that means "no limit". 15 miles is
// where assign_comps_by_proximity gives up looking, so nothing assigned to a
// deal is further out than this and the slider can always reach everything.
const MAX_RADIUS_MI = 15;

export default function PropertyPage({
  address,   // display string for the building
  records,   // every visible pin at this address
  comps,     // all comps, matched to the building by address
  S, index, total, onPrev, onNext, onClose, onShowOnMap, onOpenDeal,
}) {
  const [tabId, setTabId] = useState("summary");

  const primary = records && records.length > 0 ? records[0] : null;
  const deal = useMemo(() => (records || []).find(r => r.isPipeline) || null, [records]);
  const dealId = deal?.dealId || null;

  // ── Address dossier: property row, contacts, outreach ──────────────────
  // Fetched on demand. Sourcing properties are no longer loaded for pins, so
  // this pulls the single row rather than keeping ~1,750 in memory.
  const [dossier, setDossier] = useState({ property: null, contacts: [], outreach: [] });
  const [dossierLoading, setDossierLoading] = useState(false);

  useEffect(() => {
    if (!address) return;
    let cancelled = false;
    setDossierLoading(true);
    loadAddressDossier(String(address).split(",")[0])
      .then(d => { if (!cancelled) setDossier(d); })
      .finally(() => { if (!cancelled) setDossierLoading(false); });
    return () => { cancelled = true; };
  }, [address]);

  // ── Deal-side data ─────────────────────────────────────────────────────
  const [dealComps, setDealComps] = useState([]);

  // Filters for the assigned-comp panels. They start wide open: a comp set
  // somebody assigned is the answer until you narrow it, and a page that
  // silently hides half of it on arrival is lying about what is assigned.
  const [radiusMi, setRadiusMi] = useState(MAX_RADIUS_MI);
  const [sinceYear, setSinceYear] = useState(0);        // 0 = any date
  const [leaseType, setLeaseType] = useState("any");
  const [rentRoll, setRentRoll] = useState([]);
  const [dealLoading, setDealLoading] = useState(false);

  useEffect(() => {
    if (!dealId) { setDealComps([]); setRentRoll([]); return; }
    let cancelled = false;
    setDealLoading(true);
    Promise.all([loadAssignedComps(dealId), loadRentRoll(dealId)])
      .then(([c, rr]) => {
        if (cancelled) return;
        setDealComps(c || []);
        setRentRoll(rr || []);
      })
      .finally(() => { if (!cancelled) setDealLoading(false); });
    return () => { cancelled = true; };
  }, [dealId]);

  const saleHere = useMemo(() => saleCompsAt(comps, primary), [comps, primary]);
  const leaseNear = useMemo(() => leaseCompsNear(comps, primary, NEARBY_LEASE_METERS), [comps, primary]);

  const { property, contacts, outreach } = dossier;
  const f = k => fieldOf(property, primary, k);
  const loading = dossierLoading || dealLoading;

  // ── Headline stats, best available across every source ─────────────────
  const stats = [
    ["SF", sf(f("rba") ?? f("sf") ?? f("square_footage"))],
    ["AC Lot", num(f("land_area"))],
    ["Built", text(f("year_built"))],
    ["Class", text(f("classification"))],
    ["Clear Ht", text(f("ceiling_ht") ?? f("clear_height"))],
    deal ? ["Asking", money(pinField(deal, "askingPrice"))] : ["% Leased", pct(f("percent_leased"))],
  ].filter(([, v]) => v != null).slice(0, 6);

  const uwRows = (deal ? [
    ["Underwritten Price", money(pinField(deal, "underwrittenPrice"))],
    ["Levered IRR", pct(pinField(deal, "leveredIrr"))],
    ["Equity Multiple", mult(pinField(deal, "equityMultiple"))],
    ["Trended YoC", pct(pinField(deal, "trendedYoc"))],
    ["Untrended YoC", pct(pinField(deal, "untrendedYoc"))],
    ["Average CoC", pct(pinField(deal, "averageCoc"))],
    ["OPEX $/SF", psf(pinField(deal, "opex_psf"))],
  ] : []).filter(([, v]) => v != null);

  const sections = [
    ["Deal", deal ? [
      ["Stage", text(pinField(deal, "stage"))],
      ["Square Feet", sf(pinField(deal, "sf"))],
      ["Asking Price", money(pinField(deal, "askingPrice"))],
      ["Cap Rate", pct(pinField(deal, "capRate"))],
      ["OPEX $/SF", psf(pinField(deal, "opex_psf"))],
      ["Product Type", text(pinField(deal, "product_type"))],
      ["Marketed", pinField(deal, "marketed") == null ? null : (pinField(deal, "marketed") ? "Yes" : "No")],
      ["Call For Offers", date(pinField(deal, "call_for_offers_date"))],
    ] : []],
    // Underwriting sits beside the deal facts rather than behind a tab of its
    // own. It was four numbers in a tab nobody opened while looking at the
    // asking price, which is the one moment they mean anything - what a deal
    // is priced at and what we underwrote it to belong on the same screen.
    ["Underwriting", uwRows],
    ["Building", [
      ["RBA", sf(f("rba") ?? f("sf"))],
      ["Class", text(f("classification"))],
      ["Year Built", text(f("year_built"))],
      ["Year Renovated", text(f("year_renovated"))],
      ["Ceiling Height", text(f("ceiling_ht") ?? f("clear_height"))],
      ["Loading Docks", num(f("loading_docks"))],
      ["Drive Ins", num(f("drive_ins"))],
      ["Construction", text(f("construction_material"))],
      ["Land Area (AC)", num(f("land_area"))],
      ["% Leased", pct(f("percent_leased"))],
    ]],
    ["Sale history", [
      ["Last Sale Price", money(f("last_sale_price"))],
      ["Last Sale Date", date(f("last_sale_date"))],
      ["For Sale Status", text(f("for_sale_status"))],
      ["For Sale Price", money(f("for_sale_price"))],
    ]],
    ["Ownership", [
      ["Owner", text(f("owner_name"))],
      ["Leasing Company", text(f("leasing_company"))],
      ["Leasing Contact", text(f("leasing_contact"))],
    ]],
    ["Location", [
      ["Address", text(address)],
      ["Market", text(f("market"))],
      ["Submarket", text(f("submarket"))],
      ["City", text(f("city"))],
      ["State", text(f("state"))],
      ["Latitude", primary?.lat == null ? null : primary.lat.toFixed(5)],
      ["Longitude", primary?.lng == null ? null : primary.lng.toFixed(5)],
    ]],
  ];

  const liveSections = sections
    .map(([title, rows]) => [title, rows.filter(([, v]) => v != null)])
    .filter(([, rows]) => rows.length > 0);

  const narrative = (deal ? [
    ["Thesis", text(pinField(deal, "thesis"))],
    ["Open Questions", text(pinField(deal, "openQuestions"))],
    ["Red Flags", text(pinField(deal, "redFlags"))],
  ] : []).filter(([, v]) => v != null);


  // Assigned comps, each with its distance from this building. A comp with no
  // coordinate keeps a null distance and is never filtered out by the slider:
  // it is assigned to the deal, it simply cannot be measured, and dropping it
  // would hide it without ever saying why.
  const assignedWithDistance = useMemo(() => dealComps.map(c => ({
    ...c,
    _mi: (primary?.lat != null && primary?.lng != null && c.latitude != null && c.longitude != null)
      ? metersBetween(primary.lat, primary.lng, Number(c.latitude), Number(c.longitude)) / 1609.344
      : null,
  })), [dealComps, primary]);

  const passesFilters = (c) => {
    if (radiusMi < MAX_RADIUS_MI && c._mi != null && c._mi > radiusMi) return false;
    if (sinceYear) {
      const y = c.comp_date ? new Date(c.comp_date).getFullYear() : null;
      if (y == null || y < sinceYear) return false;
    }
    if (leaseType !== "any" && (c.type === "lease")
        && String(c.lease_type || "").toLowerCase() !== leaseType) return false;
    return true;
  };
  const assignedSale  = assignedWithDistance.filter(c => c.type === "sale"  && passesFilters(c));
  const assignedLease = assignedWithDistance.filter(c => c.type !== "sale" && passesFilters(c));
  const anySale  = dealComps.some(c => c.type === "sale");
  const anyLease = dealComps.some(c => c.type !== "sale");

  // The tables. Summary and Underwriting are no longer among them: they are
  // the page now, always on, and these are the records you page through
  // underneath. Each is a different shape of table, so only one shows at a
  // time - but choosing between them should not also hide the deal.
  const PANELS = [
    { id: "rentroll", label: "Rent Roll",      show: rentRoll.length > 0,  count: rentRoll.length },
    // `show` reads the UNFILTERED set on purpose. A pill that vanishes when
    // you drag the radius down takes its own panel with it, and you lose the
    // control you were using. It stays, and reads 0.
    { id: "assigned_sale",  label: "Assigned Sale",  show: anySale,  count: assignedSale.length },
    { id: "assigned_lease", label: "Assigned Lease", show: anyLease, count: assignedLease.length },
    { id: "lease",    label: "Lease Comps",    show: leaseNear.length > 0, count: leaseNear.length },
    { id: "sale",     label: "Sale Comps",     show: saleHere.length > 0,  count: saleHere.length },
    { id: "contacts", label: "Contacts",       show: contacts.length > 0,  count: contacts.length },
    { id: "outreach", label: "Outreach",       show: outreach.length > 0,  count: outreach.length },
  ].filter(t => t.show);
  // tabId starts at "summary", which is no longer a panel, and a deal with no
  // rent roll has no "rentroll" either - so fall back to whatever this address
  // actually has rather than showing an empty frame.
  const activePanel = PANELS.some(t => t.id === tabId) ? tabId : (PANELS[0]?.id ?? null);

  // What exists at this address, as chips under the title.
  const present = [
    deal && { label: MAP_LAYER_LABELS[deal.layerKey] || "Deal", color: MAP_LAYER_COLORS[deal.layerKey] || "#e93529" },
    saleHere.length > 0 && { label: `${saleHere.length} sale comp${saleHere.length === 1 ? "" : "s"}`, color: MAP_LAYER_COLORS.comp_sale },
    leaseNear.length > 0 && { label: `${leaseNear.length} lease comp${leaseNear.length === 1 ? "" : "s"}`, color: MAP_LAYER_COLORS.comp_lease },
    property && { label: "Property record", color: "#8a8a8a" },
  ].filter(Boolean);

  const btn = {
    background: "transparent", border: `1px solid ${S.border}`, borderRadius: 7,
    color: S.text, padding: "5px 11px", fontSize: 11, fontWeight: 700,
    fontFamily: S.font, cursor: "pointer", whiteSpace: "nowrap",
  };

  if (!address) return null;

  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 750, background: S.dark, display: "flex", flexDirection: "column", fontFamily: S.font, color: S.text, overflow: "hidden" }}>

      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: `1px solid ${S.border}`, background: S.sidebar, flexShrink: 0 }}>
        <button onClick={onClose} style={{ ...btn, border: "none", color: S.red, fontWeight: 800, paddingLeft: 0 }}>‹ Back</button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {deal && onOpenDeal && dealId && (
            <button onClick={() => onOpenDeal(dealId)} title="Open this deal in the Pipeline"
              style={{ ...btn, background: S.red, borderColor: S.red, color: "#ffffff", fontWeight: 800 }}>
              View Deal Page →
            </button>
          )}
          {onShowOnMap && primary && <button onClick={() => onShowOnMap(primary)} style={btn}>Show on map</button>}
          {total > 1 && (
            <>
              <span style={{ fontSize: 11, color: S.muted, fontVariantNumeric: "tabular-nums" }}>
                {index + 1} of {total.toLocaleString()}
              </span>
              <button onClick={onPrev} disabled={index <= 0}
                style={{ ...btn, padding: "5px 9px", opacity: index <= 0 ? 0.4 : 1, cursor: index <= 0 ? "not-allowed" : "pointer" }}>‹</button>
              <button onClick={onNext} disabled={index >= total - 1}
                style={{ ...btn, padding: "5px 9px", opacity: index >= total - 1 ? 0.4 : 1, cursor: index >= total - 1 ? "not-allowed" : "pointer" }}>›</button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 26, padding: "16px 20px 14px", borderBottom: `1px solid ${S.border}`, background: S.sidebar, flexShrink: 0, flexWrap: "wrap" }}>
        <div style={{ minWidth: 240, flexShrink: 0 }}>
          <div style={{ fontSize: 21, fontWeight: 800, letterSpacing: -0.3, lineHeight: 1.15 }}>{address}</div>
          <div style={{ fontSize: 11.5, color: S.muted, marginTop: 4 }}>
            {[f("city"), f("state")].filter(Boolean).join(", ") || f("market") || ""}
          </div>
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
            {present.map(c => (
              <span key={c.label} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 11, background: `${c.color}1f`, border: `1px solid ${c.color}55` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.color }} />
                <span style={{ fontSize: 9.5, fontWeight: 800, color: c.color }}>{c.label}</span>
              </span>
            ))}
            {loading && <span style={{ fontSize: 10, color: S.muted }}>loading…</span>}
          </div>
        </div>
        {stats.length > 0 && (
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", paddingTop: 3, borderLeft: `1px solid ${S.border}`, paddingLeft: 26 }}>
            {stats.map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>{value}</div>
                <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: S.muted, marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        )}
      </div>


      <div style={{ flex: 1, overflow: "auto", padding: `${SCROLL_PAD_TOP}px 20px 30px` }}>
        {/* Stand at the property before reading a number about it. The same
            live panorama the deal room opens with - walkable, so the question
            a photograph cannot answer ("what is next door, how does the
            loading face the street") gets answered without leaving the page.
            No photoUrl passed: the map's records carry no photo column, so
            this is Street View or a line of text saying Google has none at
            this coordinate. */}
        {primary?.lat != null && primary?.lng != null && (
          <div style={{ marginBottom: 18 }}>
            <PropertyMedia lat={primary.lat} lng={primary.lng} address={address} height={320} />
          </div>
        )}

        {/* The dashboard. Always on, never behind a tab - the deal, what we
            underwrote it to, the building and where it is, all at once. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: "4px 40px", alignItems: "start" }}>
          {liveSections.map(([title, rows]) => (
            <Section key={title} S={S} title={title}>
              {rows.map(([label, value]) => <Row key={label} S={S} label={label} value={value} />)}
            </Section>
          ))}
        </div>
        {liveSections.length === 0 && !loading && (
          <div style={{ fontSize: 12, color: S.muted }}>Nothing recorded against this address yet.</div>
        )}
        {narrative.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {narrative.map(([title, body]) => (
              <Section key={title} S={S} title={title}>
                <div style={{ fontSize: 12, lineHeight: 1.65, whiteSpace: "pre-wrap", paddingBottom: 8 }}>{body}</div>
              </Section>
            ))}
          </div>
        )}

        {/* The records underneath. Pills rather than tabs, because these sit
            inside the page now instead of replacing it. Sticky, so the choice
            stays reachable while a 200-row rent roll scrolls past it.

            THE NEGATIVE OFFSET IS NOT A FUDGE. Sticky offsets are measured from
            the scrollport's PADDING edge, and this scroller carries
            SCROLL_PAD_TOP of top padding. At top:0 the row pins that far down
            and the band above it stays uncovered - rows scroll through it and
            the panel reads as see-through. Pulling the row up by the padding
            puts its edge at the top of the visible area, and the same amount is
            added back as padding so the pills sit where they always did. */}
        {PANELS.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", position: "sticky", top: -SCROLL_PAD_TOP, zIndex: 2, background: S.dark, paddingTop: 14 + SCROLL_PAD_TOP, paddingBottom: 12, marginTop: 4 - SCROLL_PAD_TOP, borderTop: `1px solid ${S.border}` }}>
            {PANELS.map(t => {
              const on = t.id === activePanel;
              return (
                <button key={t.id} onClick={() => setTabId(t.id)}
                  style={{ background: on ? S.red : "transparent", border: `1px solid ${on ? S.red : S.border}`, borderRadius: 999, color: on ? "#ffffff" : S.muted, padding: "5px 13px", fontSize: 11.5, fontWeight: 800, fontFamily: S.font, cursor: "pointer", whiteSpace: "nowrap", transition: "background .12s, color .12s" }}>
                  {t.label}
                  {t.count != null && (
                    <span style={{ marginLeft: 6, fontWeight: 700, opacity: on ? 0.85 : 0.7, fontVariantNumeric: "tabular-nums" }}>{t.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {activePanel === "lease" && (
          <Section S={S} title={`Lease comps at or within ${NEARBY_LEASE_METERS}m`}>
            <Table S={S} head={["Sign Date", "Address", "Tenant", "Suite SF", "Rate $/SF", "Type", "OPEX $/SF", "Landlord"]}
              align={[0, 0, 0, 1, 1, 0, 1, 0]}
              rows={leaseNear.map(c => [date(c.comp_date), text(c.address), text(c.tenant),
                num(c.suite_sf ?? c.building_sf), psf(c.lease_rate), text(c.lease_type),
                psf(c.opex_psf), text(c.landlord)])} />
          </Section>
        )}

        {activePanel === "sale" && (
          <Section S={S} title="Sale comps at this address">
            <Table S={S} head={["Date", "Building SF", "Sale Price", "$/SF", "Cap", "Buyer", "Seller", "Source"]}
              align={[0, 1, 1, 1, 1, 0, 0, 0]}
              rows={saleHere.map(c => [date(c.comp_date), num(c.building_sf), money(c.sale_price),
                psf(c.price_psf), pct(c.cap_rate), text(c.buyer), text(c.seller), text(c.source)])} />
          </Section>
        )}

        {activePanel === "rentroll" && (
          <Section S={S} title={`Rent roll — ${rentRoll.length} tenant${rentRoll.length === 1 ? "" : "s"}`}>
            <Table S={S} head={["Suite", "Tenant", "Suite SF", "Rate $/SF", "Type", "Start", "End", "WALT"]}
              align={[0, 0, 1, 1, 0, 0, 0, 1]}
              rows={rentRoll.map(r => [text(r.suite_id), text(r.tenant), num(r.suite_sf),
                psf(r.lease_rate), text(r.lease_type), date(r.lease_start), date(r.lease_end), num(r.walt_years)])} />
          </Section>
        )}

        {(activePanel === "assigned_sale" || activePanel === "assigned_lease") && (
          <CompFilters
            S={S}
            radiusMi={radiusMi} setRadiusMi={setRadiusMi}
            sinceYear={sinceYear} setSinceYear={setSinceYear}
            leaseType={leaseType} setLeaseType={setLeaseType}
            showLeaseType={activePanel === "assigned_lease"}
            shown={activePanel === "assigned_sale" ? assignedSale.length : assignedLease.length}
            total={activePanel === "assigned_sale"
              ? dealComps.filter(c => c.type === "sale").length
              : dealComps.filter(c => c.type !== "sale").length}
          />
        )}

        {activePanel === "assigned_sale" && (
          <Section S={S} title="Sale comps assigned to this deal">
            <Table S={S} head={["Date", "Distance", "Address", "Building SF", "Sale Price", "$/SF", "Cap", "Buyer"]}
              align={[0, 1, 0, 1, 1, 1, 1, 0]}
              rows={assignedSale.map(c => [date(c.comp_date), miles(c._mi), text(c.address),
                num(c.building_sf ?? c.suite_sf), money(c.sale_price), psf(c.price_psf),
                pct(c.cap_rate), text(c.buyer)])} />
          </Section>
        )}

        {activePanel === "assigned_lease" && (
          <Section S={S} title="Lease comps assigned to this deal">
            <Table S={S} head={["Sign Date", "Distance", "Address", "Tenant", "Suite SF", "Rate $/SF", "Type", "Landlord"]}
              align={[0, 1, 0, 0, 1, 1, 0, 0]}
              rows={assignedLease.map(c => [date(c.comp_date), miles(c._mi), text(c.address),
                text(c.tenant), num(c.suite_sf ?? c.building_sf), psf(c.lease_rate),
                text(c.lease_type), text(c.landlord)])} />
          </Section>
        )}

        {activePanel === "contacts" && (
          <Section S={S} title="Contacts">
            <Table S={S} head={["Name", "Title", "Email", "Phone"]} align={[0, 0, 0, 0]}
              rows={contacts.map(c => [text(c.owner_name), text(c.title), text(c.email), text(c.phone)])} />
          </Section>
        )}

        {activePanel === "outreach" && (
          <Section S={S} title="Outreach history">
            <Table S={S} head={["Sent", "To", "Method", "Notes"]} align={[0, 0, 0, 0]}
              rows={outreach.map(o => [date(o.sent_at), text(o.name), text(o.method), text(o.notes)])} />
          </Section>
        )}

      </div>
    </div>
  );
}

// Distance reads in the unit people say it in. A comp in the same park is
// "0.3 mi", not "483 m", and a comp with no coordinate says so with the same
// dash every other missing value uses rather than a misleading 0.
const miles = (v) => (v == null || !Number.isFinite(v)) ? null
  : (v < 0.1 ? "<0.1 mi" : `${v.toFixed(1)} mi`);


// The controls over the assigned comp sets. One bar, shared by both panels,
// because "within 3 miles, signed since 2025" is one question about the comp
// set rather than two different questions about sale and lease.
//
// It reports what it is hiding. A filter that quietly drops rows and shows no
// trace of it is how somebody reads a 4-row comp set as the whole story.
function CompFilters({ S, radiusMi, setRadiusMi, sinceYear, setSinceYear,
                       leaseType, setLeaseType, showLeaseType, shown, total }) {
  const wrap = { display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap",
                 padding: "10px 12px", marginBottom: 14, borderRadius: 10,
                 background: S.card, border: `1px solid ${S.border}` };
  const lab = { fontSize: 10.5, fontWeight: 800, color: S.muted, letterSpacing: 0.2, textTransform: "uppercase" };
  const sel = { background: S.dark, color: S.text, border: `1px solid ${S.border}`, borderRadius: 7,
                padding: "4px 8px", fontSize: 11, fontWeight: 700, fontFamily: S.font, cursor: "pointer" };
  const atMax = radiusMi >= MAX_RADIUS_MI;
  return (
    <div style={wrap}>
      <span style={lab}>Radius</span>
      <input type="range" min={0.25} max={MAX_RADIUS_MI} step={0.25} value={radiusMi}
        onChange={(e) => setRadiusMi(Number(e.target.value))}
        style={{ width: 168, accentColor: S.red, cursor: "pointer" }} />
      <span style={{ fontSize: 11.5, fontWeight: 800, minWidth: 62, fontVariantNumeric: "tabular-nums" }}>
        {atMax ? "Any" : `${radiusMi.toFixed(2).replace(/\.?0+$/, "")} mi`}
      </span>

      <span style={{ width: 1, alignSelf: "stretch", background: S.border, margin: "0 3px" }} />

      <span style={lab}>Since</span>
      <select value={sinceYear} onChange={(e) => setSinceYear(Number(e.target.value))} style={sel}>
        <option value={0}>Any date</option>
        <option value={2023}>2023+</option>
        <option value={2024}>2024+</option>
        <option value={2025}>2025+</option>
        <option value={2026}>2026</option>
      </select>

      {showLeaseType && (
        <>
          <span style={lab}>Structure</span>
          <select value={leaseType} onChange={(e) => setLeaseType(e.target.value)} style={sel}>
            <option value="any">Any</option>
            <option value="nnn">NNN</option>
            <option value="gross">Gross</option>
          </select>
        </>
      )}

      <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ fontSize: 11, color: shown < total ? S.text : S.muted, fontVariantNumeric: "tabular-nums" }}>
          {shown === total ? `${total} assigned` : `${shown} of ${total} shown`}
        </span>
        {shown < total && (
          <button onClick={() => { setRadiusMi(MAX_RADIUS_MI); setSinceYear(0); setLeaseType("any"); }}
            style={{ ...sel, color: S.red, fontWeight: 800 }}>Clear</button>
        )}
      </span>
    </div>
  );
}


function Section({ S, title, children }) {
  return (
    <div style={{ marginBottom: 24, breakInside: "avoid" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, borderBottom: `1px solid ${S.border}`, paddingBottom: 5, marginBottom: 9 }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: S.red, letterSpacing: -0.1 }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Row({ S, label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 18, padding: "4px 0", fontSize: 12 }}>
      <span style={{ color: S.muted, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

function Table({ S, head, rows, align }) {
  if (!rows || rows.length === 0) {
    return <div style={{ fontSize: 12, color: S.muted, padding: "6px 0" }}>Nothing recorded.</div>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5, whiteSpace: "nowrap" }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={h} style={{ textAlign: align?.[i] ? "right" : "left", padding: "6px 12px 6px 0", fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: S.muted, borderBottom: `1px solid ${S.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((cell, ci) => (
                <td key={ci} style={{ textAlign: align?.[ci] ? "right" : "left", padding: "7px 12px 7px 0", borderBottom: `1px solid ${S.border}`, color: ci === 0 ? S.text : S.muted, fontWeight: ci === 0 ? 700 : 400, fontVariantNumeric: align?.[ci] ? "tabular-nums" : "normal" }}>
                  {cell ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
